import * as React from 'react';
import { usePrevious } from '../../hooks';
import { findFirstInDOM, inDOM } from '../../utils';
import { ScrollspyContext, ScrollspyContextValues } from './Scrollspy.context';

export type ScrollspyTargetRef = {
    value: string;
    element: HTMLElement;
    intersecting: boolean;
};

export interface ScrollspyRef {
    /**
     * Programmatically scroll to a target identified by the value.
     * @param value The unique identifier of the target to scroll to.
     */
    scrollToTarget: (value: string) => void;
}

export interface ScrollspyProps {
    /**
     * The child components.
     */
    children: React.ReactNode;

    /**
     * The distance from the bottom of the navigation bar that a target's top edge will be positioned when navigated to.
     * @default 20
     */
    scrollToOffset?: number;
}

export const Scrollspy = React.forwardRef<ScrollspyRef, ScrollspyProps>(
    (props: ScrollspyProps, ref): React.JSX.Element => {
        const { children, scrollToOffset = 20 } = props;

        const isClient = inDOM();

        const targetRefs = React.useRef<ScrollspyTargetRef[]>([]);
        const observerRef = React.useRef<IntersectionObserver | null>(null);
        const navigationRef = React.useRef<HTMLElement | null>(null);

        // An offset from the top edge of the viewport that we will aligns a target's top edge to
        // when the user clicks the corresponding navigation button.
        const activeTopOffset = React.useRef<number>(0);

        const [activeValue, setActiveValue] = React.useState<string | null>(null);

        const [isNavigationStuck, setIsNavigationStuck] = React.useState<boolean>(false);
        const prevIsNavigationStuck = usePrevious(isNavigationStuck);

        const handleIntersection = React.useCallback(
            (entries: IntersectionObserverEntry[]) => {
                const targets = targetRefs.current;

                // One or more targets have triggered the intersection handler.
                // ============================================================

                // Track which of the targets are intersecting.
                entries.forEach((entry) => {
                    const element = entry.target as HTMLElement;
                    const value = element.dataset.value as string;

                    const { intersectionRatio } = entry;
                    const intersecting = intersectionRatio > 0;

                    const matchIndex = targets.findIndex((item) => item.value === value);

                    if (matchIndex > -1) {
                        const target = targets[matchIndex];
                        targets[matchIndex] = {
                            ...target,
                            ...{ intersecting },
                        };
                    }
                });

                // Get all targets which are currently intersecting.
                const intersectingTargets = targetRefs.current.filter((target) => target.intersecting);
                if (intersectingTargets.length === 0) {
                    return;
                }

                // Get the current "top" of each intersecting target
                const elementTops = intersectingTargets.map((target) => {
                    const rect = target.element.getBoundingClientRect();
                    return { value: target.value, element: target.element, top: rect.top };
                });

                // Get the offset from the top edge of the viewport that we consider a target to be active.
                const offsetFromTop = activeTopOffset.current;

                // Find the target which has its top edge closest to the offset.
                const closestTarget = elementTops.reduce((acc, curr) => {
                    return Math.abs(curr.top - offsetFromTop) < Math.abs(acc.top - offsetFromTop) ? curr : acc;
                }, elementTops[0]);

                // Set the closest target as our active item.
                const { element } = closestTarget;
                const value = element.dataset.value as string;

                setActiveValue(value);
            },
            [setActiveValue]
        );

        const checkActiveTarget = React.useCallback(() => {
            if (!isNavigationStuck) {
                // Targets may not be added in DOM order. Each time a new target is added or removed, check
                // which of the target is first in the DOM and set that to be the active target.
                const firstTarget = findFirstInDOM(targetRefs.current.map((target) => target.element));
                const firstValue = targetRefs.current.find((target) => target.element === firstTarget);

                if (firstValue) {
                    setActiveValue(firstValue.value);
                }
            }
        }, [isNavigationStuck]);

        // Child scrollspy target components will register/deregister their root element when they are mounted/unmounted.
        const handleRegisterTarget = React.useCallback(
            (element: HTMLElement) => {
                const value = element.dataset.value as string;

                // Start observing this target.
                observerRef.current?.observe(element);

                // Add this target to our targets array
                targetRefs.current.push({
                    value,
                    element,
                    intersecting: false,
                });

                checkActiveTarget();
            },
            [checkActiveTarget]
        );

        const handleDeregisterTarget = React.useCallback(
            (element: HTMLElement) => {
                const value = element.dataset.value as string;

                // Stop observing this target.
                observerRef.current?.unobserve(element);

                targetRefs.current = targetRefs.current.filter((target) => target.value !== value);

                checkActiveTarget();
            },
            [checkActiveTarget]
        );

        const handleNavigationRef = React.useCallback(
            (element: HTMLElement) => {
                if (!isClient || !element) {
                    return;
                }
                navigationRef.current = element;
                activeTopOffset.current = element.getBoundingClientRect().height + scrollToOffset;
            },
            [isClient, scrollToOffset]
        );

        const handleNavigationStuckChange = React.useCallback((stuck: boolean) => {
            setIsNavigationStuck(stuck);
        }, []);

        const handleScrollToTarget = React.useCallback((value: string) => {
            const target = targetRefs.current.find((item) => item.value === value);
            const navigation = navigationRef.current;
            if (!target || !navigation) {
                return;
            }

            const offsetFromTop = activeTopOffset.current;

            const topPosition = target.element.getBoundingClientRect().top + window.scrollY - offsetFromTop;
            window.scrollTo({ top: topPosition, behavior: 'smooth' });
        }, []);

        const handleButtonClick = React.useCallback(
            (value: string) => {
                handleScrollToTarget(value);
            },
            [handleScrollToTarget]
        );

        React.useEffect(() => {
            if (isNavigationStuck !== prevIsNavigationStuck) {
                if (isNavigationStuck) {
                    // The navigation bar is now stuck to the top edge of the viewport.
                    // Create the intersection observer and start observing the targets.
                    observerRef.current = new IntersectionObserver(handleIntersection, {
                        root: null,
                        threshold: [0, 0.25, 0.5, 0.75, 0.99],
                    });

                    targetRefs.current.forEach((target) => {
                        observerRef.current?.observe(target.element);
                    });
                } else {
                    // Disconnect the observer
                    observerRef.current?.disconnect();
                }
            }
        }, [handleIntersection, isNavigationStuck, prevIsNavigationStuck, setActiveValue]);

        React.useImperativeHandle(ref, () => ({
            scrollToTarget: handleScrollToTarget,
        }));

        // onmount
        React.useEffect(() => {
            return () => {
                // on unmount
                observerRef.current?.disconnect();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const context: ScrollspyContextValues = React.useMemo(
            () => ({
                activeValue,
                navigationRef: handleNavigationRef,
                isNavigationStuck,
                onButtonClick: handleButtonClick,
                onNavigationStuckChange: handleNavigationStuckChange,
                registerTarget: handleRegisterTarget,
                deregisterTarget: handleDeregisterTarget,
            }),
            [
                activeValue,
                handleButtonClick,
                handleDeregisterTarget,
                handleNavigationRef,
                handleNavigationStuckChange,
                handleRegisterTarget,
                isNavigationStuck,
            ]
        );

        return <ScrollspyContext.Provider value={context}>{children}</ScrollspyContext.Provider>;
    }
);
